import { useCallback, useRef, useState } from 'react'
import {
  FileImage,
  Scaling,
  CropIcon,
  Eraser,
  Copy,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateModel } from './context'
import type { PreprocessingOp } from './types'

// ─── Mock Preprocessing Runner ──────────────────────────────────

function useMockPreprocess() {
  const { state, dispatch } = useCreateModel()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const run = useCallback(
    (op: PreprocessingOp, onComplete: () => void) => {
      if (state.preprocessingOp) return // already running

      dispatch({ type: 'START_PREPROCESSING', payload: op })
      let progress = 0
      const total = state.images.length

      intervalRef.current = setInterval(() => {
        progress++
        dispatch({ type: 'PREPROCESSING_PROGRESS', payload: progress })

        if (progress >= total) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          onComplete()
          dispatch({ type: 'FINISH_PREPROCESSING' })
        }
      }, 300)
    },
    [state.images.length, state.preprocessingOp, dispatch],
  )

  return { run, isRunning: state.preprocessingOp !== null }
}

// ─── Operation Labels ───────────────────────────────────────────

const OP_LABELS: Record<PreprocessingOp, string> = {
  convert: 'Converting',
  resize: 'Resizing',
  crop: 'Cropping',
  'remove-bg': 'Removing backgrounds',
  'remove-duplicates': 'Scanning duplicates',
}

// ─── Toolbar Component ──────────────────────────────────────────

export function PreprocessToolbar() {
  const { state, dispatch } = useCreateModel()
  const { run, isRunning } = useMockPreprocess()
  const [targetFormat, setTargetFormat] = useState<string>('png')
  const [targetSize, setTargetSize] = useState<string>('1024')
  const [cropMode, setCropMode] = useState<string>('square')

  if (state.images.length === 0) return null

  // Compute stats for smart suggestions
  const formats = new Set(state.images.map((i) => i.format))
  const hasMixedFormats = formats.size > 1
  const hasLargeImages = state.images.some((i) => Math.max(i.width, i.height) > 2048)
  const hasBadAspect = state.images.some((i) => i.issues.some((iss) => iss.type === 'bad-aspect' || iss.type === 'too-tall'))
  const duplicateCount = state.images.filter((i) => i.issues.some((iss) => iss.type === 'duplicate')).length

  // ── Convert handler ──
  const handleConvert = () => {
    run('convert', () => {
      // Mock: update all image formats
      for (const img of state.images) {
        dispatch({
          type: 'UPDATE_IMAGE',
          payload: { imageId: img.id, updates: { format: targetFormat } },
        })
      }
    })
  }

  // ── Resize handler ──
  const handleResize = () => {
    const maxDim = parseInt(targetSize, 10)
    run('resize', () => {
      for (const img of state.images) {
        if (Math.max(img.width, img.height) > maxDim) {
          const scale = maxDim / Math.max(img.width, img.height)
          dispatch({
            type: 'UPDATE_IMAGE',
            payload: {
              imageId: img.id,
              updates: {
                width: Math.round(img.width * scale),
                height: Math.round(img.height * scale),
              },
            },
          })
        }
      }
    })
  }

  // ── Crop handler ──
  const handleCrop = () => {
    run('crop', () => {
      for (const img of state.images) {
        let newW = img.width
        let newH = img.height

        if (cropMode === 'square') {
          const min = Math.min(img.width, img.height)
          newW = min
          newH = min
        } else if (cropMode === '4:3') {
          if (img.width / img.height > 4 / 3) {
            newW = Math.round(img.height * (4 / 3))
          } else {
            newH = Math.round(img.width / (4 / 3))
          }
        } else if (cropMode === '3:4') {
          if (img.height / img.width > 4 / 3) {
            newH = Math.round(img.width * (4 / 3))
          } else {
            newW = Math.round(img.height / (4 / 3))
          }
        }

        dispatch({
          type: 'UPDATE_IMAGE',
          payload: {
            imageId: img.id,
            updates: {
              width: newW,
              height: newH,
              issues: img.issues.filter((iss) => iss.type !== 'bad-aspect' && iss.type !== 'too-tall'),
            },
          },
        })
      }
    })
  }

  // ── Remove BG handler ──
  const handleRemoveBg = () => {
    run('remove-bg', () => {
      // Mock: convert all to PNG (transparent bg)
      for (const img of state.images) {
        dispatch({
          type: 'UPDATE_IMAGE',
          payload: { imageId: img.id, updates: { format: 'png' } },
        })
      }
    })
  }

  // ── Remove Duplicates handler ──
  const handleRemoveDuplicates = () => {
    const dupIds = state.images
      .filter((i) => i.issues.some((iss) => iss.type === 'duplicate'))
      .map((i) => i.id)

    if (dupIds.length === 0) return

    run('remove-duplicates', () => {
      dispatch({ type: 'REMOVE_IMAGES_BULK', payload: dupIds })
    })
  }

  // ── Currently running indicator ──
  if (isRunning && state.preprocessingOp) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span className="text-xs font-medium">
          {OP_LABELS[state.preprocessingOp]}... {state.preprocessingProgress}/{state.images.length}
        </span>
        <Progress
          value={(state.preprocessingProgress / state.images.length) * 100}
          size="sm"
          className="flex-1"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Prepare dataset
      </p>

      {/* Tool buttons row */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Convert format */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="outline"
            size="sm"
            className={cn('h-7 gap-1.5 px-2 text-[11px]', hasMixedFormats && 'border-amber-500/50 text-amber-500')}
            onClick={handleConvert}
          >
            <FileImage className="h-3 w-3" />
            Convert
            {hasMixedFormats && <span className="rounded bg-amber-500/10 px-1 text-[9px]">{formats.size} types</span>}
          </Button>
          <Select value={targetFormat} onValueChange={setTargetFormat}>
            <SelectTrigger className="h-7 w-16 border-0 bg-transparent px-1 text-[11px] shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value="png">PNG</SelectItem>
              <SelectItem value="jpeg">JPG</SelectItem>
              <SelectItem value="webp">WebP</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Resize */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="outline"
            size="sm"
            className={cn('h-7 gap-1.5 px-2 text-[11px]', hasLargeImages && 'border-amber-500/50 text-amber-500')}
            onClick={handleResize}
          >
            <Scaling className="h-3 w-3" />
            Resize
            {hasLargeImages && <span className="rounded bg-amber-500/10 px-1 text-[9px]">needed</span>}
          </Button>
          <Select value={targetSize} onValueChange={setTargetSize}>
            <SelectTrigger className="h-7 w-16 border-0 bg-transparent px-1 text-[11px] shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value="512">512px</SelectItem>
              <SelectItem value="1024">1024px</SelectItem>
              <SelectItem value="2048">2048px</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Crop */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="outline"
            size="sm"
            className={cn('h-7 gap-1.5 px-2 text-[11px]', hasBadAspect && 'border-amber-500/50 text-amber-500')}
            onClick={handleCrop}
          >
            <CropIcon className="h-3 w-3" />
            Crop
          </Button>
          <Select value={cropMode} onValueChange={setCropMode}>
            <SelectTrigger className="h-7 w-16 border-0 bg-transparent px-1 text-[11px] shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value="square">1:1</SelectItem>
              <SelectItem value="4:3">4:3</SelectItem>
              <SelectItem value="3:4">3:4</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Remove background */}
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2 text-[11px]"
          onClick={handleRemoveBg}
        >
          <Eraser className="h-3 w-3" />
          Remove BG
        </Button>

        {/* Remove duplicates */}
        {duplicateCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 border-amber-500/50 px-2 text-[11px] text-amber-500"
            onClick={handleRemoveDuplicates}
          >
            <Copy className="h-3 w-3" />
            {duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Format summary */}
      <div className="flex flex-wrap gap-1.5">
        {Array.from(formats).map((fmt) => {
          const count = state.images.filter((i) => i.format === fmt).length
          return (
            <span key={fmt} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              {fmt.toUpperCase()} ({count})
            </span>
          )
        })}
        {state.images.length > 0 && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            {(state.images.reduce((s, i) => s + i.sizeBytes, 0) / 1024 / 1024).toFixed(1)} MB total
          </span>
        )}
      </div>
    </div>
  )
}
